import type {
  EncoderElixhauserMeasure,
  EncoderElixhauserResult,
  EncoderHacAlert,
  MedicareCodeEdit,
  MedicareCodeEditorCodeResult,
  MedicareCodeEditorResponse,
  MsDrgDetails,
  MsDrgGroupResponse,
} from "../types/encoder";
import { unwrapEncoderPayload } from "./encoder";

const isRecord = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === "object" && !Array.isArray(value);

const asList = (value: unknown): unknown[] =>
  Array.isArray(value) ? value : [];

const detailText = (
  details: Record<string, unknown> | null,
  keys: string[]
): string => {
  if (!details) return "";
  for (let i = 0; i < keys.length; i += 1) {
    const value = details[keys[i]];
    if (value == null) continue;
    const text = String(value).trim();
    if (text && text !== "undefined" && text !== "null") return text;
  }
  return "";
};

export const formatEncoderGrouperVersion = (version: unknown): string => {
  if (version == null || version === "") return "";
  const value = String(version);
  if (value.indexOf(".") >= 0) return value;
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue) || numericValue < 100) return value;
  return `${Math.floor(numericValue / 10)}.${numericValue % 10}`;
};

export const buildMsDrgDisplayDetails = (
  rawDetails: unknown,
  grouperDetails: unknown,
  description?: string | null
): MsDrgDetails => {
  const details = isRecord(rawDetails) ? rawDetails : null;
  const grouper = isRecord(grouperDetails) ? grouperDetails : null;
  const finalMdc =
    grouper && isRecord(grouper.finalMdc) ? grouper.finalMdc : null;
  const medSurg = String(
    grouper ? grouper.finalMedicalSurgicalType ?? "" : ""
  ).toUpperCase();
  const typeFromGrouper =
    medSurg === "SURGICAL" ? "SURG" : medSurg === "MEDICAL" ? "MED" : "";
  const mdcFromGrouper =
    finalMdc && finalMdc.value != null && String(finalMdc.value).trim()
      ? String(finalMdc.value)
      : "";
  const mdcDescriptionFromGrouper =
    finalMdc && finalMdc.description != null
      ? String(finalMdc.description).trim()
      : "";

  return {
    MDC: detailText(details, ["MDC", "mdc"]) || mdcFromGrouper,
    MDCDescription:
      detailText(details, ["MDCDescription", "mdcDescription"]) ||
      mdcDescriptionFromGrouper,
    TYPE: detailText(details, ["TYPE", "type"]) || typeFromGrouper,
    "MS-DRG Title":
      detailText(details, ["MS-DRG Title", "MS_DRG_Title", "title"]) ||
      (description || "").trim(),
    Weights: detailText(details, ["Weights", "weights", "WEIGHT"]),
    GLOS: detailText(details, ["GLOS", "glos", "GMLOS", "gmlos"]),
    ALOS: detailText(details, ["ALOS", "alos", "AMLOS", "amlos"]),
    DOLLARS: detailText(details, ["DOLLARS", "dollars", "Payment", "payment"]),
  };
};

export const extractMsDrgGroupResult = (
  payload: unknown
): MsDrgGroupResponse => {
  const data = unwrapEncoderPayload(payload);
  if (!data) {
    return {
      success: false,
      value: null,
      description: null,
      details: {},
    };
  }
  const nested = isRecord(data.data) ? data.data : null;
  const grouperDetails = isRecord(data.grouperDetails)
    ? data.grouperDetails
    : nested && isRecord(nested.grouperDetails)
      ? nested.grouperDetails
      : null;
  const rawDetails =
    data.details != null
      ? data.details
      : nested && nested.details != null
        ? nested.details
        : null;
  const description =
    typeof data.description === "string"
      ? data.description
      : nested && typeof nested.description === "string"
        ? nested.description
        : null;
  const value =
    (data.value as number | null | undefined) ??
    (nested ? (nested.value as number | null | undefined) : undefined) ??
    null;

  return {
    success: data?.success !== false,
    error: typeof data?.error === "string" ? data.error : undefined,
    value,
    description,
    details: buildMsDrgDisplayDetails(rawDetails, grouperDetails, description),
    grouperDetails: grouperDetails ?? undefined,
  };
};

export const getEncoderHacAlerts = (
  grouperDetails: unknown
): EncoderHacAlert[] => {
  const grouper = isRecord(grouperDetails) ? grouperDetails : null;
  const rows: unknown[] = [];
  if (grouper && isRecord(grouper.principalDiagnosis)) {
    rows.push(grouper.principalDiagnosis);
  }
  const secondary = asList(grouper ? grouper.secondaryDiagnoses : undefined);
  for (let i = 0; i < secondary.length; i += 1) rows.push(secondary[i]);

  const alerts: EncoderHacAlert[] = [];
  for (let i = 0; i < rows.length; i += 1) {
    const candidate = rows[i];
    const row = isRecord(candidate) ? candidate : null;
    if (!row) continue;
    const code = String(row["code"] ?? "");
    const hacs = asList(row["hacs"]);
    for (let j = 0; j < hacs.length; j += 1) {
      const hacCandidate = hacs[j];
      const hac = isRecord(hacCandidate) ? hacCandidate : null;
      if (!hac) continue;
      if (String(hac["status"] ?? "").toUpperCase() !== "HAC_CRITERIA_MET") {
        continue;
      }
      const number = hac["number"] != null ? String(hac["number"]) : "";
      const label = number ? `HAC ${number}` : "HAC";
      const description = String(hac["description"] ?? "").trim();
      const listMessage = String(hac["list"] ?? "").trim();
      const redundantList =
        Boolean(number) &&
        /^HAC[-_ ]?0*\d+$/i.test(listMessage) &&
        String(Number(listMessage.replace(/^HAC[-_ ]?/i, ""))) === number;
      const parts = [label, description];
      if (listMessage && !redundantList) parts.push(listMessage);
      alerts.push({
        code,
        message: parts.filter(Boolean).join(" - "),
      });
    }
  }
  return alerts;
};

const asCodeResult = (
  value: unknown,
  fallbackRole: string
): MedicareCodeEditorCodeResult | null => {
  const row = isRecord(value) ? value : null;
  if (!row) return null;
  const code = String(row.value ?? row.code ?? "").trim();
  if (!code) return null;
  const edits = asList(row.edits)
    .map((item): MedicareCodeEdit | null => {
      const edit = isRecord(item) ? item : null;
      if (!edit) return null;
      return {
        index: typeof edit.index === "number" ? edit.index : undefined,
        code: edit.code != null ? String(edit.code) : undefined,
        description:
          edit.description != null ? String(edit.description) : undefined,
        reason: edit.reason != null ? String(edit.reason) : null,
      };
    })
    .filter((item): item is MedicareCodeEdit => Boolean(item));
  return {
    value: code,
    role: String(row.role ?? fallbackRole),
    edits,
    editFlags: row.editFlags != null ? String(row.editFlags) : null,
    ageConflictType:
      row.ageConflictType != null ? String(row.ageConflictType) : null,
  };
};

export const extractMedicareCodeEditorResult = (
  payload: unknown
): MedicareCodeEditorResponse => {
  const data = unwrapEncoderPayload(payload);
  const diagnoses = asList(data?.diagnoses)
    .map((item) => asCodeResult(item, "diagnosis"))
    .filter((item): item is MedicareCodeEditorCodeResult => Boolean(item));
  const procedures = asList(data?.procedures)
    .map((item) => asCodeResult(item, "procedure"))
    .filter((item): item is MedicareCodeEditorCodeResult => Boolean(item));
  return {
    success: data?.success !== false,
    error: typeof data?.error === "string" ? data.error : undefined,
    versionUsed:
      typeof data?.versionUsed === "number" ? data.versionUsed : undefined,
    editType: data?.editType != null ? String(data.editType) : undefined,
    admissionDiagnosis: asCodeResult(
      data?.admissionDiagnosis,
      "admissionDiagnosis"
    ),
    diagnoses,
    procedures,
  };
};

export const medicareCodeEditAlerts = (
  result: MedicareCodeEditorResponse | null | undefined
): MedicareCodeEditorCodeResult[] => {
  if (!result) return [];
  const rows: MedicareCodeEditorCodeResult[] = [];
  if (result.admissionDiagnosis) rows.push(result.admissionDiagnosis);
  const diagnoses = result.diagnoses ?? [];
  for (let i = 0; i < diagnoses.length; i += 1) rows.push(diagnoses[i]);
  const procedures = result.procedures ?? [];
  for (let i = 0; i < procedures.length; i += 1) rows.push(procedures[i]);
  return rows.filter((row) => row.edits.length > 0);
};

const asMeasures = (value: unknown): EncoderElixhauserMeasure[] =>
  asList(value)
    .map((item) => (isRecord(item) ? (item as EncoderElixhauserMeasure) : null))
    .filter((item): item is EncoderElixhauserMeasure => Boolean(item));

export const extractElixhauserResult = (
  payload: unknown
): EncoderElixhauserResult | null => {
  const data = unwrapEncoderPayload(payload);
  if (!data) return null;
  const nested = isRecord(data.data) ? data.data : null;
  const source =
    isRecord(data.indices) || Array.isArray(data.triggered_measures)
      ? data
      : nested &&
          (isRecord(nested.indices) || Array.isArray(nested.triggered_measures))
        ? nested
        : data;
  return {
    indices: isRecord(source.indices)
      ? (source.indices as EncoderElixhauserResult["indices"])
      : undefined,
    triggered_measures: asMeasures(
      source.triggered_measures ?? source.triggeredMeasures
    ),
    suppressed_measures: asMeasures(
      source.suppressed_measures ?? source.suppressedMeasures
    ),
    rejected_measures: asMeasures(
      source.rejected_measures ?? source.rejectedMeasures
    ),
  };
};
